import { useId, useState } from "react";

import { Button, Spinner } from "@sirdab/ui";

import { useApi } from "../api";
import { useMessages } from "../i18n";

export interface PhotoUploaderProps {
  merchantSlug: string;
  /** The verified session the upload endpoint requires. Travels as a bearer header. */
  shopperToken: string;
  /** `uploadId`s already attached to this line. The files themselves are never held. */
  uploadIds: readonly string[];
  onChange: (next: string[]) => void;
  /** Already-localised group label. */
  label: string;
  required?: boolean;
}

/** Generous, and enforced again by the server, which constrains size and MIME (docs/06). */
const MAX_BYTES = 8 * 1024 * 1024;

/**
 * Evidence upload (docs/08 step 4). One call: the bytes go up as multipart to
 * `POST /api/v1/returns/shopper/merchants/:merchantSlug/uploads` and only the returned
 * `uploadId` is kept — the portal never carries image data into the submit payload, and
 * nothing is persisted in the browser.
 *
 * Every failure is caught and stated. A silent upload failure on a blocking step is
 * precisely the dead end that sends a shopper to WhatsApp.
 */
export function PhotoUploader({
  merchantSlug,
  shopperToken,
  uploadIds,
  onChange,
  label,
  required = false,
}: PhotoUploaderProps): React.JSX.Element {
  const api = useApi();
  const { t } = useMessages();
  const inputId = useId();
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  async function handleFile(file: File): Promise<void> {
    setFailed(false);
    if (file.size > MAX_BYTES || !file.type.startsWith("image/")) {
      setFailed(true);
      return;
    }
    setBusy(true);
    const sent = await api.uploadEvidence({ merchantSlug, file }, shopperToken);
    setBusy(false);
    if (!sent.ok) {
      setFailed(true);
      return;
    }
    onChange([...uploadIds, sent.data.uploadId]);
  }

  return (
    <div className="flex flex-col gap-2">
      <p className="text-label text-foreground">{label}</p>

      {uploadIds.length === 0 ? (
        <p className="text-caption-quiet text-muted-foreground">
          {t("evidence.no_photos")}
        </p>
      ) : (
        <ul className="flex flex-col gap-1">
          {uploadIds.map((id, index) => (
            <li
              key={id}
              className="text-caption bg-muted flex items-center justify-between gap-2 rounded-md px-2 py-1"
            >
              <span dir="ltr" className="truncate text-foreground">
                {`${t("evidence.photos_label")} ${index + 1}`}
              </span>
              <Button
                type="button"
                variant="link"
                size="sm"
                onClick={() =>
                  onChange(uploadIds.filter((value) => value !== id))
                }
              >
                {t("action.remove_photo")}
              </Button>
            </li>
          ))}
        </ul>
      )}

      <input
        id={inputId}
        type="file"
        accept="image/*"
        className="hidden"
        aria-label={t("action.add_photo")}
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file !== undefined) void handleFile(file);
        }}
      />
      <Button
        type="button"
        variant="secondary"
        size="xl"
        disabled={busy}
        onClick={() => document.getElementById(inputId)?.click()}
        className="self-start"
      >
        {t("action.add_photo")}
      </Button>

      {busy ? (
        <p className="text-caption-quiet text-muted-foreground flex items-center gap-2">
          <Spinner size="sm" />
          {t("evidence.uploading")}
        </p>
      ) : null}
      {failed ? (
        <p role="alert" className="text-caption text-destructive">
          {t("evidence.upload_failed")}
        </p>
      ) : null}
      {required && uploadIds.length === 0 ? (
        <p className="text-caption-quiet text-muted-foreground">
          {t("evidence.required_note")}
        </p>
      ) : null}
    </div>
  );
}
